import { ApiError } from "../apiError";
import { tokenRepository } from "../tokenRepository";

const REQUEST_TIMEOUT_MS = 30_000;

type ParamValue = string | number | boolean | null | undefined;

export type QueryParams = Record<string, ParamValue>;

export interface V2ApiClient {
  get: <T>(path: string, params?: QueryParams, signal?: AbortSignal) => Promise<T>;
  post: <T>(path: string, body?: unknown, params?: QueryParams) => Promise<T>;
}

interface V2ApiClientDeps {
  baseUrl: string;
  getToken: () => Promise<string>;
  fetchImpl?: typeof fetch;
}

interface RequestOptions {
  method: "GET" | "POST";
  params?: QueryParams;
  body?: unknown;
  signal?: AbortSignal;
}

// bundled modules are strict, where a bare global fetch loses the receiver the browser requires
const globalFetch: typeof fetch = (input, init) => fetch(input, init);

export const createV2ApiClient = ({
  baseUrl,
  getToken,
  fetchImpl = globalFetch
}: V2ApiClientDeps): V2ApiClient => {
  const request = async <T>(
    path: string,
    { method, params = {}, body, signal }: RequestOptions
  ): Promise<T> => {
    const url = new URL(path, baseUrl);

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    });

    // fetch has no timeout of its own, and jsdom has no AbortSignal.timeout to build one from
    const timeoutController = new AbortController();
    const timeoutId = signal
      ? undefined
      : setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT_MS);

    try {
      const hasBody = body !== undefined;
      const response = await fetchImpl(url.toString(), {
        method,
        headers: {
          Authorization: `Bearer ${await getToken()}`,
          ...(hasBody && { "Content-Type": "application/json" })
        },
        body: hasBody ? JSON.stringify(body) : undefined,
        signal: signal ?? timeoutController.signal
      });

      const text = await response.text();

      if (!response.ok) {
        throw new ApiError(response.status, url.pathname, text);
      }

      // a 204 or an empty body would make JSON.parse throw
      if (!text) {
        return undefined as T;
      }

      try {
        return JSON.parse(text) as T;
      } catch {
        // a malformed body should still surface as ApiError, not a bare SyntaxError
        throw new ApiError(response.status, url.pathname, text);
      }
    } finally {
      clearTimeout(timeoutId);
    }
  };

  return {
    get: (path, params, signal) => request(path, { method: "GET", params, signal }),
    post: (path, body, params) => request(path, { method: "POST", params, body })
  };
};

export const v2ApiClient = createV2ApiClient({
  // v2 is served from the same host as the Supabase project in every environment
  baseUrl: process.env.NX_SUPABASE_PROJECT_URL,
  getToken: tokenRepository.getAccessToken
});
